/**
 * This file contains prop => style functions related to a UI elements
 * appearance.
 */
import { stringLiteralArray } from "./utils"
import {
  BackgroundProperty,
  BorderProperty,
  StandardShorthandProperties,
  StandardLonghandProperties
  // eslint-disable-next-line import/no-unresolved
} from "csstype"
import { pick } from "ramda"

type BackgroundProperties = { background?: BackgroundProperty<string> } & Pick<
  StandardLonghandProperties,
  typeof backgroundLonghandProperties[number]
>
export type Background = BackgroundProperty<string> | BackgroundProperties
export type BackgroundProp = {
  /**
   * The CSS background style.
   */
  background?: Background
}

type BorderShorthandProperties = Pick<
  StandardShorthandProperties,
  typeof borderShorthandProperties[number]
>

type BorderLonghandProperties = Pick<
  StandardLonghandProperties,
  typeof borderLonghandProperties[number]
>
type BorderProperties = {
  border?: BorderProperty<string | 0>
} & BorderShorthandProperties &
  BorderLonghandProperties
export type Border = BorderProperty<string | 0> | BorderProperties
export type BorderProp = {
  /**
   * The CSS border style.
   */
  border?: Border
}

export type Props = BackgroundProp & BorderProp

const backgroundLonghandProperties = stringLiteralArray([
  "backgroundAttachment",
  "backgroundBlendMode",
  "backgroundClip",
  "backgroundColor",
  "backgroundImage",
  "backgroundOrigin",
  "backgroundPosition",
  "backgroundPositionX",
  "backgroundPositionY",
  "backgroundRepeat",
  "backgroundSize"
])

const backgroundProperties = stringLiteralArray([
  "background",
  ...backgroundLonghandProperties
])

const borderShorthandProperties = stringLiteralArray([
  "borderBottom",
  "borderColor",
  "borderImage",
  "borderLeft",
  "borderRadius",
  "borderRight",
  "borderStyle",
  "borderTop",
  "borderWidth"
])

const borderLonghandProperties = stringLiteralArray([
  "borderBottomColor",
  "borderBottomLeftRadius",
  "borderBottomRightRadius",
  "borderBottomStyle",
  "borderBottomWidth",
  "borderCollapse",
  "borderImageOutset",
  "borderImageRepeat",
  "borderImageSlice",
  "borderImageSource",
  "borderImageWidth",
  "borderLeftColor",
  "borderLeftStyle",
  "borderLeftWidth",
  "borderRightColor",
  "borderRightStyle",
  "borderRightWidth",
  "borderSpacing",
  "borderTopColor",
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderTopStyle",
  "borderTopWidth"
])

const borderProperties = stringLiteralArray([
  "border",
  ...borderShorthandProperties,
  ...borderLonghandProperties
])

const background = ({
  background
}: Record<string, any>):
  | {}
  | { background: BorderProperty<string | 0> }
  | BackgroundProperties =>
  background === undefined
    ? {}
    : typeof background === "string"
    ? { background }
    : pick(backgroundProperties, background)

const border = ({
  border
}: Record<string, any>):
  | {}
  | { border: BorderProperty<string | 0> }
  | BorderProperties =>
  border === undefined
    ? {}
    : typeof border === "string"
    ? { border }
    : pick(borderProperties, border)

const interpolations = { background, border }

export default interpolations
